<div *hasBoltOn="[environment.stripe.shaas21products.bo_ai_moderation]"
    class="h-full flex-col items-center justify-center w-full overflow-hidden">

    @if (isLoading()) {
    <atom-loading-inline title="Loading" message="Building your brief retrospective..."></atom-loading-inline>
    } @else if (noContentForRetro()) {
    <div class="w-full h-full flex items-center justify-center overflow-y-auto px-4 py-8 sm:px-8">
        <div class="w-full max-w-3xl">
            <div
                class="rounded-2xl border border-zinc-200 bg-linear-to-b from-teal-500/5 to-transparent p-6 sm:p-10 dark:border-zinc-800 dark:from-teal-400/10">
                <div class="text-center">
                    <span
                        class="inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-800 ring-1 ring-inset ring-teal-600/20 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-500/20">
                        <span class="size-1.5 rounded-full bg-teal-500 animate-pulse" aria-hidden="true"></span>
                        Waiting for results
                    </span>

                    <div class="relative mx-auto mt-6 flex size-20 items-center justify-center">
                        <div class="absolute inset-0 rounded-full bg-teal-500/10 dark:bg-teal-400/15" aria-hidden="true">
                        </div>
                        <div class="absolute inset-2 rounded-full bg-teal-500/5 dark:bg-teal-400/10" aria-hidden="true">
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="relative size-9 text-teal-600 dark:text-teal-400" aria-hidden="true">
                            <path
                                d="M12.034 12.681a.498.498 0 0 1 .647-.647l9 3.5a.5.5 0 0 1-.033.943l-3.444 1.068a1 1 0 0 0-.66.66l-1.067 3.443a.5.5 0 0 1-.943.033z" />
                            <path d="M5 3a2 2 0 0 0-2 2" />
                            <path d="M19 3a2 2 0 0 1 2 2" />
                            <path d="M5 21a2 2 0 0 1-2-2" />
                            <path d="M9 3h1" />
                            <path d="M9 21h2" />
                            <path d="M14 3h1" />
                            <path d="M3 9v1" />
                            <path d="M21 9v2" />
                            <path d="M3 14v1" />
                        </svg>
                    </div>

                    <h2 class="mt-6 text-2xl font-semibold text-primary use-grotesque">Brief Retrospective</h2>
                    <p class="mx-auto mt-3 max-w-lg text-sm leading-6 text-secondary sm:text-base">
                        Once the brief has results, we analyse your best-performing videos and build a retrospective to
                        help you understand what worked.
                    </p>
                </div>

                <p class="mt-8 text-center text-xs font-semibold uppercase tracking-wide text-secondary">
                    What you'll see here
                </p>

                <ul role="list" class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                    <li
                        class="flex gap-3 rounded-xl border border-zinc-200 bg-white/70 p-4 text-left dark:border-zinc-800 dark:bg-zinc-900/50">
                        <div
                            class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 dark:bg-teal-400/15">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-5 text-teal-600 dark:text-teal-400" aria-hidden="true">
                                <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
                                <rect x="2" y="6" width="14" height="12" rx="2" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="text-sm font-semibold text-primary">Top Performing Videos</h3>
                            <p class="mt-1 text-xs leading-5 text-secondary">
                                The videos that performed best according to the brief goal.
                            </p>
                        </div>
                    </li>
                    <li
                        class="flex gap-3 rounded-xl border border-zinc-200 bg-white/70 p-4 text-left dark:border-zinc-800 dark:bg-zinc-900/50">
                        <div
                            class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 dark:bg-teal-400/15">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-5 text-teal-600 dark:text-teal-400" aria-hidden="true">
                                <path d="M12 3v3" />
                                <path d="M18.5 5.5 16 8" />
                                <path d="M21 12h-3" />
                                <path d="M18.5 18.5 16 16" />
                                <path d="M12 21v-3" />
                                <path d="M5.5 18.5 8 16" />
                                <path d="M3 12h3" />
                                <path d="M5.5 5.5 8 8" />
                                <circle cx="12" cy="12" r="4" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="text-sm font-semibold text-primary">Shared Traits</h3>
                            <p class="mt-1 text-xs leading-5 text-secondary">
                                Traits the top performers share from their AI reviews.
                            </p>
                        </div>
                    </li>
                    <li
                        class="flex gap-3 rounded-xl border border-zinc-200 bg-white/70 p-4 text-left dark:border-zinc-800 dark:bg-zinc-900/50">
                        <div
                            class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 dark:bg-teal-400/15">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-5 text-teal-600 dark:text-teal-400" aria-hidden="true">
                                <path
                                    d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
                                <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="text-sm font-semibold text-primary">Content Themes</h3>
                            <p class="mt-1 text-xs leading-5 text-secondary">
                                An AI summary of what top-performing videos share creatively.
                            </p>
                        </div>
                    </li>
                    <li
                        class="flex gap-3 rounded-xl border border-zinc-200 bg-white/70 p-4 text-left dark:border-zinc-800 dark:bg-zinc-900/50">
                        <div
                            class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 dark:bg-teal-400/15">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-5 text-teal-600 dark:text-teal-400" aria-hidden="true">
                                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                                <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                                <path d="M10 9H8" />
                                <path d="M16 13H8" />
                                <path d="M16 17H8" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="text-sm font-semibold text-primary">Suggested Next Brief</h3>
                            <p class="mt-1 text-xs leading-5 text-secondary">
                                A follow-up brief based on what worked in your top videos.
                            </p>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
    } @else {

    <div class="h-full flex-col space-y-4 items-center justify-center w-full overflow-y-auto scrollbar-transparent">

        <!-- Top Performing Videos -->
        @if (topPerformers().length > 0) {
        <div
            class="w-full divide-y divide-zinc-200 dark:divide-zinc-600 overflow-hidden rounded-lg bg-white dark:bg-gray-800 card-container-pale">
            <div class="px-2 pb-3 flex flex-row items-center justify-between">
                <div class="flex flex-col">
                    <h3 class="text-sm font-semibold text-primary">Top Performing Videos</h3>
                    <p class="mt-1 text-xs text-secondary">The videos that performed best according to the brief goal.
                    </p>
                </div>
                <div class="flex flex-col items-end text-right">
                    <h3 class="text-sm font-semibold text-primary">{{ focussedBrief().brief_goal_name }}</h3>
                    <p class="mt-1 text-xs text-secondary">{{ focussedBrief().brief_goal_description }}</p>
                </div>
            </div>
            <div class="px-2 py-3 overflow-x-auto">
                <ul class="flex flex-row gap-x-4">
                    @for (performer of topPerformers(); track $index) {
                    <li
                        class="relative shrink-0 w-1/7 bg-white dark:bg-gray-700 rounded-lg p-2 border border-gray-200 dark:border-gray-600 flex flex-col self-start">
                        <atom-media-viewer-simpler
                            class="w-full group overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary"
                            [src]="performer.coverImage"
                            [inputClass]="'h-full pointer-events-none aspect-9/16 object-cover group-hover:opacity-75 rounded-t-lg'"
                            [post]="$any(performer.post)" w="full"></atom-media-viewer-simpler>
                        <p class="text-xs font-medium text-primary line-clamp-1 mt-2">{{ performer.handle }}</p>
                        <p class="text-xs text-secondary">{{ performer.metricDisplay }}</p>
                    </li>
                    }
                </ul>
            </div>
        </div>
        }
        <!-- Top Performing Videos - END -->

        <!-- What these videos have in common -->
        <div
            class="w-full divide-y divide-zinc-200 dark:divide-zinc-600 overflow-hidden rounded-lg bg-white dark:bg-gray-800 card-container-pale">
            <div class="px-2 pb-3 flex flex-row items-center justify-between">
                <div class="flex flex-col">
                    <h3 class="text-sm font-semibold text-primary">What these videos have in common</h3>
                    <p class="mt-1 text-xs text-secondary">Traits the top performers share, from their AI reviews.</p>
                </div>
                @if (commonalities()) {
                <span
                    class="inline-flex items-center rounded-md bg-primary/10 dark:bg-primary/25 px-2 py-1 text-xs font-medium text-accent shrink-0">
                    {{ commonalities()!.totalVideosCompared }} videos compared
                </span>
                }
            </div>

            @if (noCommonalities()) {
            <div class="px-2 py-8 flex flex-col gap-y-3 items-center justify-center text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    aria-hidden="true" class="size-12 text-secondary">
                    <path d="M10.66 6H14a2 2 0 0 1 2 2v2.5l5.248-3.062A.5.5 0 0 1 22 7.87v8.196" />
                    <path d="M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2" />
                    <path d="m2 2 20 20" />
                </svg>
                <h3 class="text-sm font-semibold text-primary">No shared traits found</h3>
                <p class="text-sm text-secondary">The top performers don't share any AI-review items in common yet.</p>
            </div>
            } @else {
            <div class="px-2 py-3 grid grid-cols-1 lg:grid-cols-2 gap-4">

                <!-- Done well (Approved) -->
                <section class="flex flex-col gap-y-3">
                    <div class="flex items-center gap-2">
                        <span
                            class="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/30 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-600/20">
                            Done well
                        </span>
                        <p class="text-xs text-secondary">Items the top videos consistently satisfied.</p>
                    </div>
                    @if (commonalities()!.approvedCommonalities.length > 0) {
                    <div class="flex flex-col gap-y-3">
                        @for (item of commonalities()!.approvedCommonalities; track $index) {
                        <div
                            class="flex flex-col w-full items-start bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 gap-y-2">
                            <div class="flex flex-row items-start justify-between w-full gap-x-2">
                                <div class="flex flex-row items-start gap-x-2">
                                    @if (item.type) {
                                    <span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium shrink-0"
                                        [class.bg-blue-50]="item.type === 'do'"
                                        [class.text-blue-700]="item.type === 'do'"
                                        [class.dark:bg-blue-900/30]="item.type === 'do'"
                                        [class.dark:text-blue-300]="item.type === 'do'"
                                        [class.bg-primary/10]="item.type === 'dont'"
                                        [class.text-accent]="item.type === 'dont'"
                                        [class.dark:bg-primary/25]="item.type === 'dont'">
                                        {{ item.type === 'do' ? 'Do' : "Don't" }}
                                    </span>
                                    }
                                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ item.statement }}
                                    </p>
                                </div>
                                <span
                                    class="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/30 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-600/20 shrink-0">
                                    {{ item.count }} / {{ item.totalCompared }}
                                </span>
                            </div>
                            <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                <div class="bg-green-500 dark:bg-green-600 h-2 rounded-full transition-all duration-300"
                                    [style.width.%]="item.percentage"></div>
                            </div>
                        </div>
                        }
                    </div>
                    } @else {
                    <p class="text-xs text-secondary italic px-1">No shared strengths across the top videos.</p>
                    }
                </section>

                <!-- Commonly missed (Issue found) -->
                <section class="flex flex-col gap-y-3">
                    <div class="flex items-center gap-2">
                        <span
                            class="inline-flex items-center rounded-md bg-amber-50 dark:bg-amber-900/30 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 ring-1 ring-inset ring-amber-600/20">
                            Commonly missed
                        </span>
                        <p class="text-xs text-secondary">Gaps even the top videos share.</p>
                    </div>
                    @if (commonalities()!.issueCommonalities.length > 0) {
                    <div class="flex flex-col gap-y-3">
                        @for (item of commonalities()!.issueCommonalities; track $index) {
                        <div
                            class="flex flex-col w-full items-start bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 gap-y-2">
                            <div class="flex flex-row items-start justify-between w-full gap-x-2">
                                <div class="flex flex-row items-start gap-x-2">
                                    @if (item.type) {
                                    <span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium shrink-0"
                                        [class.bg-blue-50]="item.type === 'do'"
                                        [class.text-blue-700]="item.type === 'do'"
                                        [class.dark:bg-blue-900/30]="item.type === 'do'"
                                        [class.dark:text-blue-300]="item.type === 'do'"
                                        [class.bg-primary/10]="item.type === 'dont'"
                                        [class.text-accent]="item.type === 'dont'"
                                        [class.dark:bg-primary/25]="item.type === 'dont'">
                                        {{ item.type === 'do' ? 'Do' : "Don't" }}
                                    </span>
                                    }
                                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ item.statement }}
                                    </p>
                                </div>
                                <span
                                    class="inline-flex items-center rounded-md bg-amber-50 dark:bg-amber-900/30 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 ring-1 ring-inset ring-amber-600/20 shrink-0">
                                    {{ item.count }} / {{ item.totalCompared }}
                                </span>
                            </div>
                            <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                <div class="bg-amber-500 dark:bg-amber-600 h-2 rounded-full transition-all duration-300"
                                    [style.width.%]="item.percentage"></div>
                            </div>
                        </div>
                        }
                    </div>
                    } @else {
                    <p class="text-xs text-secondary italic px-1">No shared gaps across the top videos.</p>
                    }
                </section>

            </div>
            }
        </div>
        <!-- What these videos have in common - END -->

        <!-- Content Themes in Common (AI) -->
        <div
            class="w-full divide-y divide-zinc-200 dark:divide-zinc-600 overflow-hidden rounded-lg bg-white dark:bg-gray-800 card-container-pale">
            <div class="px-2 pb-3">
                <h3 class="text-sm font-semibold text-primary">Content Themes in Common</h3>
                <p class="mt-1 text-xs text-secondary">An AI summary of what the top-performing videos share creatively.
                </p>
            </div>

            @if (isLoadingContentSummary()) {
            <div class="px-2 py-8 flex flex-col gap-y-3 items-center justify-center text-center">
                <svg aria-hidden="true"
                    class="mx-auto w-12 h-12 text-primary/15 animate-spin dark:text-gray-600 fill-primary"
                    viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor" />
                    <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill" />
                </svg>
                <p class="text-sm text-secondary">Finding common content themes...</p>
            </div>
            } @else if (noContentSummary()) {
            <div class="px-2 py-8 flex flex-col gap-y-3 items-center justify-center text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    aria-hidden="true" class="size-12 text-secondary">
                    <path d="M10.66 6H14a2 2 0 0 1 2 2v2.5l5.248-3.062A.5.5 0 0 1 22 7.87v8.196" />
                    <path d="M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2" />
                    <path d="m2 2 20 20" />
                </svg>
                <h3 class="text-sm font-semibold text-primary">No common content themes found</h3>
                <p class="text-sm text-secondary">There isn't enough content data to summarise shared themes.</p>
            </div>
            } @else if (contentSummary()) {
            <dl class="px-2 py-3 divide-y divide-zinc-200 dark:divide-zinc-600">
                <div class="py-3 first:pt-0 last:pb-0">
                    <dt class="text-sm font-semibold text-primary">Actions</dt>
                    <dd class="mt-1 text-sm text-secondary">{{ contentSummary()!.actionsSummary }}</dd>
                </div>
                <div class="py-3 first:pt-0 last:pb-0">
                    <dt class="text-sm font-semibold text-primary">Overall Tone</dt>
                    <dd class="mt-1 text-sm text-secondary">{{ contentSummary()!.overallToneSummary }}</dd>
                </div>
                <div class="py-3 first:pt-0 last:pb-0">
                    <dt class="text-sm font-semibold text-primary">Content Type</dt>
                    <dd class="mt-1 text-sm text-secondary">{{ contentSummary()!.contentTypeSummary }}</dd>
                </div>
                <div class="py-3 first:pt-0 last:pb-0">
                    <dt class="text-sm font-semibold text-primary">Keywords</dt>
                    <dd class="mt-1 text-sm text-secondary">{{ contentSummary()!.keywordsByIndustrySummary }}</dd>
                </div>
                <div class="py-3 first:pt-0 last:pb-0">
                    <dt class="text-sm font-semibold text-primary">Products</dt>
                    <dd class="mt-1 text-sm text-secondary">{{ contentSummary()!.productsShownSummary }}</dd>
                </div>
            </dl>
            }
        </div>
        <!-- Content Themes in Common (AI) - END -->

        <!-- Suggested Next Brief -->
        @if (!noCommonalities() || !noContentSummary()) {
        <div
            class="w-full divide-y divide-zinc-200 dark:divide-zinc-600 overflow-hidden rounded-lg bg-white dark:bg-gray-800 card-container-pale">
            <div class="px-2 pb-3">
                <h3 class="text-sm font-semibold text-primary">Suggested Next Brief</h3>
                <p class="mt-1 text-xs text-secondary">A follow-up brief based on what worked in your top-performing
                    videos.</p>
            </div>

            @if (suggestedBriefLoadingState() === 'loading') {
            <div class="px-2 py-8 flex flex-col gap-y-3 items-center justify-center text-center">
                <svg aria-hidden="true"
                    class="mx-auto w-12 h-12 text-primary/15 animate-spin dark:text-gray-600 fill-primary"
                    viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor" />
                    <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill" />
                </svg>
                <p class="text-sm text-secondary">Building your suggested next brief…</p>
            </div>
            } @else if (suggestedBriefLoadingState() === 'error') {
            <div class="px-2 py-8 flex flex-col gap-y-3 items-center justify-center text-center">
                <h4 class="text-sm font-semibold text-primary">Could not generate a suggested brief</h4>
                <p class="text-sm text-secondary">{{ suggestedBriefError() }}</p>
                <button type="button" class="primary-button" (click)="retrySuggestedBrief()">Try again</button>
            </div>
            } @else if (suggestedBrief()) {
            <div class="px-2 py-3 flex flex-col gap-y-4">
                <p class="text-sm text-secondary">
                    We analysed your top-performing videos — the review criteria they consistently passed ("Done well")
                    and the creative themes they share — then used SHOUT AI to draft a follow-up brief that encodes those
                    winning patterns for your next creators.
                </p>
                <div class="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3">
                    <h4 class="text-sm font-semibold text-primary">Synopsis</h4>
                    <p class="mt-1 text-sm text-secondary">{{ suggestedBrief()!.synopsis }}</p>
                </div>
                <div>
                    <button type="button" class="primary-button" (click)="openSuggestedBriefInBuilder()">
                        Review suggested brief
                    </button>
                </div>
            </div>
            }
        </div>
        }
        <!-- Suggested Next Brief - END -->

    </div>
    }
</div>



<div *notHasBoltOn="[environment.stripe.shaas21products.bo_ai_moderation]"
    class="h-full w-full overflow-auto border-2 border-dashed border-primary rounded-lg">
    <div
        class="mx-auto flex h-full w-full max-w-6xl flex-col items-stretch gap-10 px-4 py-8 lg:flex-row lg:items-center lg:gap-0 lg:px-8 lg:py-10">
        <article class="flex flex-1 flex-col justify-center lg:max-w-xl lg:pr-10">
            <div class="mx-auto w-full max-w-lg text-center lg:mx-0 lg:text-left">
                <div
                    class="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/25 mb-6 lg:mx-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-10 text-accent">
                        <path
                            d="M12.034 12.681a.498.498 0 0 1 .647-.647l9 3.5a.5.5 0 0 1-.033.943l-3.444 1.068a1 1 0 0 0-.66.66l-1.067 3.443a.5.5 0 0 1-.943.033z" />
                        <path d="M5 3a2 2 0 0 0-2 2" />
                        <path d="M19 3a2 2 0 0 1 2 2" />
                        <path d="M5 21a2 2 0 0 1-2-2" />
                        <path d="M9 3h1" />
                        <path d="M9 21h2" />
                        <path d="M14 3h1" />
                        <path d="M3 9v1" />
                        <path d="M21 9v2" />
                        <path d="M3 14v1" />
                    </svg>
                </div>
                <h2 class="text-2xl font-semibold text-primary mb-4">Brief Retrospective</h2>
                <p class="text-base text-secondary leading-relaxed mb-6">
                    Once the brief has been completed, we can review the results and provide a retrospective of the
                    brief.
                </p>
                <div class="space-y-3 text-left max-w-md mx-auto lg:mx-0">
                    <div class="flex items-start gap-3">
                        <div class="shrink-0 mt-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="text-accent">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                        </div>
                        <p class="text-sm text-secondary">
                            <span class="font-semibold text-primary">Top Performing Videos:</span> The videos that
                            performed best according to the brief goal.
                        </p>
                    </div>
                    <div class="flex items-start gap-3">
                        <div class="shrink-0 mt-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="text-accent">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                        </div>
                        <p class="text-sm text-secondary">
                            <span class="font-semibold text-primary">Questions In Common:</span> The questions that are
                            common to all of the videos.
                        </p>
                    </div>
                    <div class="flex items-start gap-3">
                        <div class="shrink-0 mt-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="text-accent">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                        </div>
                        <p class="text-sm text-secondary">
                            <span class="font-semibold text-primary">What do these videos have in common?</span> Each
                            video has been analysed, and the common traits are explained.
                        </p>
                    </div>
                    <div class="flex items-start gap-3">
                        <div class="shrink-0 mt-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="text-accent">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                        </div>
                        <p class="text-sm text-secondary">
                            <span class="font-semibold text-primary">Choosing creators?</span> We'll suggest creators
                            that are similar to the top performers.
                        </p>
                    </div>
                </div>
            </div>
        </article>

        <div class="hidden lg:block w-px shrink-0 self-stretch bg-zinc-200 dark:bg-zinc-800" aria-hidden="true"></div>

        <!-- Has Subscription - No Bolt On -->
        <article *hasBoltOn="[environment.stripe.shaas21products.sub_essential]"
            class="flex flex-1 flex-col justify-center lg:pl-10">
            <div
                class="mx-auto w-full max-w-md space-y-6 rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/60 dark:bg-zinc-900/40 p-6 lg:p-8">

                <div class="text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="mx-auto size-10 text-gray-400 dark:text-gray-500 mb-3" aria-hidden="true">
                        <path d="M12 2a10 10 0 0 1 7.38 16.75" />
                        <path d="m16 12-4-4-4 4" />
                        <path d="M12 16V8" />
                        <path d="M2.5 8.875a10 10 0 0 0-.5 3" />
                        <path d="M2.83 16a10 10 0 0 0 2.43 3.4" />
                        <path d="M4.636 5.235a10 10 0 0 1 .891-.857" />
                        <path d="M8.644 21.42a10 10 0 0 0 7.631-.38" />
                    </svg>

                    <h2 class="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white mb-2">
                        <span class="text-transparent bg-clip-text bg-linear-to-r from-[#ff80b5] to-[#9089fc]">Upgrade
                            your
                            plan</span>
                    </h2>
                    <p class="text-zinc-600 dark:text-zinc-400 text-sm">
                        Add AI Video Moderation to your plan and keep your content compliant.
                    </p>
                </div>

                <div
                    class="w-full rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/60 backdrop-blur-sm shadow-sm dark:shadow-none p-5">
                    <div class="flex items-start gap-3 mb-4">
                        <div
                            class="shrink-0 w-10 h-10 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-6 text-zinc-600 dark:text-white">
                                <path
                                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="text-base font-semibold text-zinc-900 dark:text-white">AI Video Moderation</h3>
                            <p class="text-sm text-zinc-600 dark:text-zinc-400 mt-0.5">Unlimited moderations · Use AI to
                                review videos</p>
                        </div>
                    </div>
                    <ul class="space-y-2 text-sm text-zinc-600 dark:text-zinc-300">
                        <li class="flex items-center gap-2">
                            <svg class="size-4 shrink-0 text-accent" viewBox="0 0 20 20" fill="currentColor"
                                aria-hidden="true">
                                <path
                                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                    clip-rule="evenodd" fill-rule="evenodd" />
                            </svg>
                            Content reviewed against your brief
                        </li>
                        <li class="flex items-center gap-2">
                            <svg class="size-4 shrink-0 text-accent" viewBox="0 0 20 20" fill="currentColor"
                                aria-hidden="true">
                                <path
                                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                    clip-rule="evenodd" fill-rule="evenodd" />
                            </svg>
                            Suggested fixes &amp; accuracy score
                        </li>
                        <li class="flex items-center gap-2">
                            <svg class="size-4 shrink-0 text-accent" viewBox="0 0 20 20" fill="currentColor"
                                aria-hidden="true">
                                <path
                                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                    clip-rule="evenodd" fill-rule="evenodd" />
                            </svg>
                            Works with your Compliance Library
                        </li>
                    </ul>
                </div>

                <button type="button" (click)="openChoosePlan()"
                    class="inline-flex w-full items-center justify-center gap-x-1.5 flashy-go-button">
                    Upgrade AI Moderation
                    <svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true"
                        class="-mr-0.5 size-5">
                        <path
                            d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                            clip-rule="evenodd" fill-rule="evenodd" />
                    </svg>
                </button>
            </div>
        </article>

        <!-- Has NO Subscription -->
        <article *notHasBoltOn="[environment.stripe.shaas21products.sub_essential]"
            class="flex flex-1 flex-col justify-center lg:pl-10">
            <div
                class="mx-auto w-full max-w-md space-y-6 rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/60 dark:bg-zinc-900/40 p-6 lg:p-8">

                <div class="text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="mx-auto size-10 text-gray-400 dark:text-gray-500 mb-3" aria-hidden="true">
                        <path d="M12 2a10 10 0 0 1 7.38 16.75" />
                        <path d="m16 12-4-4-4 4" />
                        <path d="M12 16V8" />
                        <path d="M2.5 8.875a10 10 0 0 0-.5 3" />
                        <path d="M2.83 16a10 10 0 0 0 2.43 3.4" />
                        <path d="M4.636 5.235a10 10 0 0 1 .891-.857" />
                        <path d="M8.644 21.42a10 10 0 0 0 7.631-.38" />
                    </svg>

                    <h2 class="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white mb-2">
                        <span class="text-transparent bg-clip-text bg-linear-to-r from-[#ff80b5] to-[#9089fc]">Start
                            your
                            free trial</span>
                    </h2>
                    <p class="text-zinc-600 dark:text-zinc-400 text-sm">
                        Get AI Video Moderation and keep your content compliant from day one.
                    </p>
                </div>

                <div
                    class="w-full rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/60 backdrop-blur-sm shadow-sm dark:shadow-none p-5">
                    <div class="flex items-start gap-3 mb-4">
                        <div
                            class="shrink-0 w-10 h-10 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-6 text-zinc-600 dark:text-white">
                                <path
                                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="text-base font-semibold text-zinc-900 dark:text-white">AI Video Moderation</h3>
                            <p class="text-sm text-zinc-600 dark:text-zinc-400 mt-0.5">Unlimited moderations · Use AI to
                                review videos</p>
                        </div>
                    </div>
                    <ul class="space-y-2 text-sm text-zinc-600 dark:text-zinc-300">
                        <li class="flex items-center gap-2">
                            <svg class="size-4 shrink-0 text-accent" viewBox="0 0 20 20" fill="currentColor"
                                aria-hidden="true">
                                <path
                                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                    clip-rule="evenodd" fill-rule="evenodd" />
                            </svg>
                            Content reviewed against your brief
                        </li>
                        <li class="flex items-center gap-2">
                            <svg class="size-4 shrink-0 text-accent" viewBox="0 0 20 20" fill="currentColor"
                                aria-hidden="true">
                                <path
                                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                    clip-rule="evenodd" fill-rule="evenodd" />
                            </svg>
                            Suggested fixes &amp; accuracy score
                        </li>
                        <li class="flex items-center gap-2">
                            <svg class="size-4 shrink-0 text-accent" viewBox="0 0 20 20" fill="currentColor"
                                aria-hidden="true">
                                <path
                                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                    clip-rule="evenodd" fill-rule="evenodd" />
                            </svg>
                            Works with your Compliance Library
                        </li>
                    </ul>
                </div>

                <button type="button" (click)="openChoosePlan()"
                    class="inline-flex w-full items-center justify-center gap-x-1.5 flashy-go-button">
                    Start Free Trial
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="-mr-0.5 size-5" aria-hidden="true">
                        <path d="M12 2a10 10 0 0 1 7.38 16.75" />
                        <path d="m16 12-4-4-4 4" />
                        <path d="M12 16V8" />
                        <path d="M2.5 8.875a10 10 0 0 0-.5 3" />
                        <path d="M2.83 16a10 10 0 0 0 2.43 3.4" />
                        <path d="M4.636 5.235a10 10 0 0 1 .891-.857" />
                        <path d="M8.644 21.42a10 10 0 0 0 7.631-.38" />
                    </svg>
                </button>
            </div>
        </article>

    </div>
</div>